import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, Target, Shield, Award, Users, Zap } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Shield size={28} />,
      title: 'Integrity',
      description: 'We operate with transparency and honesty in every engagement.'
    },
    {
      icon: <Award size={28} />,
      title: 'Technical Excellence',
      description: 'We deliver solutions built on best practices and proven methodologies.'
    },
    {
      icon: <Users size={28} />,
      title: 'Client Success',
      description: 'Your growth and success are the measures of our achievement.'
    },
    {
      icon: <Zap size={28} />,
      title: 'Scalability',
      description: 'Solutions designed to grow and adapt with your business.'
    }
  ];

  return (
    <>
      {/* Page Header */}
      <section className="page-header" data-testid="about-header">
        <div className="container">
          <h1>About Arvix Consulting</h1>
          <p>
            A modern technology consulting firm delivering practical solutions 
            that create real business value.
          </p>
        </div>
      </section>

      {/* About Intro */}
      <section className="section" data-testid="about-intro-section">
        <div className="container">
          <div className="about-intro">
            <div className="about-content">
              <h2>Engineering Clarity Across the Product Lifecycle</h2>
              <p className="text-muted mb-md">
                Arvix Consulting is a technology consulting and digital solutions company 
                helping businesses navigate complex technical challenges. We combine deep 
                enterprise engineering experience with modern digital expertise to deliver 
                solutions that drive measurable results.
              </p>
              <p className="text-muted mb-md">
                From startups building their first web presence to enterprises implementing 
                sophisticated engineering data systems, we partner with clients at every 
                stage of their technology journey.
              </p>
              <p className="text-muted">
                Our approach is grounded in practical experience and a commitment to 
                understanding your unique business context. We don't just deliver 
                technology—we deliver outcomes that matter.
              </p>
            </div>
            <div className="about-image">
              <img 
                src="https://images.unsplash.com/photo-1770943558988-2c605d6bd5a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBjb25zdWx0aW5nJTIwdGVhbSUyMG1lZXRpbmclMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzcyMTg1OTY4fDA&ixlib=rb-4.1.0&q=85" 
                alt="Arvix Consulting Office"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="section section-alt" data-testid="vision-mission-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Vision & Mission</h2>
            <p>
              Guiding principles that shape every solution we deliver.
            </p>
          </div>
          <div className="vision-mission">
            <div className="vm-card" data-testid="vision-card">
              <h3>
                <Eye size={24} />
                Our Vision
              </h3>
              <p>
                To be the trusted technology partner for businesses seeking practical, 
                scalable solutions that drive sustainable growth. We envision a future 
                where technology complexity becomes clarity, enabling businesses of all 
                sizes to compete effectively in the digital economy.
              </p>
            </div>
            <div className="vm-card" data-testid="mission-card">
              <h3>
                <Target size={24} />
                Our Mission
              </h3>
              <p>
                To deliver technology consulting services that combine enterprise-grade 
                expertise with agile execution. We are committed to understanding each 
                client's unique context and crafting solutions that address real business 
                challenges while positioning them for future success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section" data-testid="values-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Core Values</h2>
            <p>
              The principles that guide our work and relationships.
            </p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card" data-testid={`value-${index}`}>
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="cta-banner" data-testid="about-cta-section">
        <div className="container">
          <h2>Let's Work Together</h2>
          <p>
            Ready to discuss how we can help your business achieve its technology goals?
          </p>
          <Link to="/contact" className="btn btn-white" data-testid="about-cta-btn">
            Start a Conversation
          </Link>
        </div>
      </section>
    </>
  );
};

export default About;
